/**
 * Turns a server error message into something fit for a toast.
 *
 * The backend writes Go-style messages — lower-case first letter, no
 * full stop, and now and then Go's slice formatting for a list
 * ("no payroll run found for: [Aug 2026 Sep 2026]"). toApiError() runs
 * every message through here, so every `toast.error(toApiError(e).message)`
 * reads as a sentence:
 *
 *   "this entity has no PF establishment code"
 *     → "This entity has no PF establishment code."
 *   "no payroll run found for: [Aug 2026 Sep 2026 Oct 2026]"
 *     → "No payroll run found for: Aug 2026, Sep 2026 and Oct 2026."
 *   "employees missing a UAN: [12 15]" → "Employees missing a UAN: 12 and 15."
 *
 * Messages that already read well pass through unchanged (apart from the
 * capital and the full stop). Plain .ts with no imports so it unit-tests
 * under `npm run test:unit` (humanize.test.ts).
 */

/** "Aug 2026" — the month labels Go's period guards list. */
const MONTH_ITEM = /[A-Z][a-z]{2,8} \d{4}/g;
const MONTH_LIST = /^(?:[A-Z][a-z]{2,8} \d{4})(?: [A-Z][a-z]{2,8} \d{4})*$/;
/** A bracketed list that has no nested brackets: Go's %v of a slice. */
const GO_SLICE = /\[([^[\]]+)\]/g;

/** "A", "A and B", "A, B and C". */
export function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function sliceItems(inner: string): string[] {
  const s = inner.trim();
  if (MONTH_LIST.test(s)) return s.match(MONTH_ITEM) ?? [s];
  if (s.includes(",")) return s.split(",").map((x) => x.trim()).filter(Boolean);
  return s.split(/\s+/).filter(Boolean);
}

export function humanizeApiMessage(message: string | null | undefined): string {
  let m = (message ?? "").trim();
  if (!m) return m;
  m = m.replace(GO_SLICE, (_whole, inner: string) => joinList(sliceItems(inner)));
  m = m.charAt(0).toUpperCase() + m.slice(1);
  if (/[A-Za-z0-9)]$/.test(m)) m += ".";
  return m;
}
